// Import model
import { Lead } from '../models/Lead';

// Import base class serializer

// Import dependency types (models and enums used in properties)
import { LeadIntegration } from '../models/LeadIntegration';
import { ObjectAppointment } from '../models/ObjectAppointment';
import { Organization } from '../models/Organization';
import { User } from '../models/User';

// Import dependency serializers (for complex types)
import { LeadIntegrationSerializer } from './LeadIntegrationSerializer';
import { ObjectAppointmentSerializer } from './ObjectAppointmentSerializer';
import { OrganizationSerializer } from './OrganizationSerializer';
import { UserSerializer } from './UserSerializer';

// Import derived type serializers (for polymorphic deserialization)

/**
 * Serializer for Lead - handles JSON serialization and deserialization with type validation
 */
export class LeadSerializer {
  /**
   * Deserializes a JSON string into a Lead instance
   */
  static deserialize(json: string): Lead {
    const obj = JSON.parse(json);
    return this.fromObject(obj);
  }

  /**
   * Deserializes a plain object into a Lead instance
   */
  static fromObject(obj: any): Lead {
    if (!obj) {
      throw new Error('Cannot deserialize null or undefined to Lead');
    }


    const instance: Lead = {
      id: obj['_id'] as string | undefined,
      fullName: obj['fullName'] as string | undefined,
      phone: obj['phone'] as string | undefined,
      email: obj['email'] as string | undefined,
      address: obj['address'] as string | undefined,
      city: obj['city'] as string | undefined,
      state: obj['state'] as string | undefined,
      postalCode: obj['postalCode'] as string | undefined,
      country: obj['country'] as string | undefined,
      userId: obj['userId'] as string | undefined,
      userIdName: obj['userId|Name'] as string | undefined,
      nextAppointmentId: obj['nextAppointmentId'] as string | undefined,
      nextAppointmentIdName: obj['nextAppointmentId|Name'] as string | undefined,
      objectStatusId: obj['objectStatusId'] as string | undefined,
      objectStatusIdName: obj['objectStatusId|Name'] as string | undefined,
      organizationId: obj['organizationId'] as string | undefined,
      organizationIdName: obj['organizationId|Name'] as string | undefined,
      sfLeadSourceId: obj['sfLeadSourceId'] as string | undefined,
      sfLeadSourceIdName: obj['sfLeadSourceId|Name'] as string | undefined,
      leadTypeId: obj['leadTypeId'] as string | undefined,
      leadTypeIdName: obj['leadTypeId|Name'] as string | undefined,
      phonePreferences: obj['phonePreferences'] as string | undefined,
      smsPreferences: obj['smsPreferences'] as string | undefined,
      emailPreferences: obj['emailPreferences'] as string | undefined,
      convertedDate: obj['convertedDate'] as string | undefined,
      isActive: obj['isActive'] as boolean | undefined,
      flowId: obj['flowId'] as string | undefined,
      flowIdName: obj['flowId|Name'] as string | undefined,
      notes: obj['notes'] as string | undefined,
      tags: obj['tags'] ? (obj['tags'] as any[]).map((item: any) =>
        item as string
      ) : [] as any,
      createdDate: obj['createdDate'] as string | undefined,
      lastModifiedDate: obj['lastModifiedDate'] as string | undefined,
      timeZoneId: obj['timeZoneId'] as string | undefined,
      integrations: obj['integrations'] ? (obj['integrations'] as any[]).map((item: any) =>
        LeadIntegrationSerializer.fromObject(item)
      ) : [] as any,
      sfLeadExternalId: obj['sfLeadExternalId'] as any | undefined,
      sfAccountExternalId: obj['sfAccountExternalId'] as any | undefined,
      location: obj['Location'] as any | undefined,
      locationDistance: obj['LocationDistance'] as any | undefined,
      service: obj['service'] as string | undefined,
      appointments: obj['appointments'] ? (obj['appointments'] as any[]).map((item: any) =>
        ObjectAppointmentSerializer.fromObject(item)
      ) : [] as any,
      organization: obj['organization'] ? OrganizationSerializer.fromObject(obj['organization']) : undefined as any,
      user: obj['user'] ? UserSerializer.fromObject(obj['user']) : undefined as any,
      nextAppointment: obj['nextAppointment'] ? ObjectAppointmentSerializer.fromObject(obj['nextAppointment']) : undefined as any,
    };

    return instance;
  }

  /**
   * Deserializes a JSON string into an array of Lead instances
   */
  static deserializeArray(json: string): Lead[] {
    const arr = JSON.parse(json);
    if (!Array.isArray(arr)) {
      throw new Error('Expected JSON array');
    }
    return arr.map(obj => this.fromObject(obj));
  }

  /**
   * Serializes a Lead instance to a JSON string
   */
  static serialize(instance: Lead): string {
    return JSON.stringify(this.toObject(instance));
  }

  /**
   * Converts a Lead instance to a plain object
   */
  static toObject(instance: Lead): any {
    return {
      '_id': instance.id,
      'fullName': instance.fullName,
      'phone': instance.phone,
      'email': instance.email,
      'address': instance.address,
      'city': instance.city,
      'state': instance.state,
      'postalCode': instance.postalCode,
      'country': instance.country,
      'userId': instance.userId,
      'userId|Name': instance.userIdName,
      'nextAppointmentId': instance.nextAppointmentId,
      'nextAppointmentId|Name': instance.nextAppointmentIdName,
      'objectStatusId': instance.objectStatusId,
      'objectStatusId|Name': instance.objectStatusIdName,
      'organizationId': instance.organizationId,
      'organizationId|Name': instance.organizationIdName,
      'sfLeadSourceId': instance.sfLeadSourceId,
      'sfLeadSourceId|Name': instance.sfLeadSourceIdName,
      'leadTypeId': instance.leadTypeId,
      'leadTypeId|Name': instance.leadTypeIdName,
      'phonePreferences': instance.phonePreferences,
      'smsPreferences': instance.smsPreferences,
      'emailPreferences': instance.emailPreferences,
      'convertedDate': instance.convertedDate,
      'isActive': instance.isActive,
      'flowId': instance.flowId,
      'flowId|Name': instance.flowIdName,
      'notes': instance.notes,
      'tags': instance.tags?.map((item: any) =>
        item
      ),
      'createdDate': instance.createdDate,
      'lastModifiedDate': instance.lastModifiedDate,
      'timeZoneId': instance.timeZoneId,
      'integrations': instance.integrations?.map((item: any) =>
        LeadIntegrationSerializer.toObject(item)
      ),
      'sfLeadExternalId': instance.sfLeadExternalId,
      'sfAccountExternalId': instance.sfAccountExternalId,
      'Location': instance.location,
      'LocationDistance': instance.locationDistance,
      'service': instance.service,
      'appointments': instance.appointments?.map((item: any) =>
        ObjectAppointmentSerializer.toObject(item)
      ),
      'organization': instance.organization ? OrganizationSerializer.toObject(instance.organization) : undefined,
      'user': instance.user ? UserSerializer.toObject(instance.user) : undefined,
      'nextAppointment': instance.nextAppointment ? ObjectAppointmentSerializer.toObject(instance.nextAppointment) : undefined,
    };
  }

  /**
   * Serializes an array of Lead instances to a JSON string
   */
  static serializeArray(instances: Lead[]): string {
    return JSON.stringify(instances.map(inst => this.toObject(inst)));
  }
}
